// The one place a range source becomes a fetch: a curated dataset through GET /api/datasets/{id}/query. Used by the
// Custom View fetchers (preview, combined chart, cards, accuracy) and the Ops dashboard. Kept apart from
// seriesSources.ts so the pure rules can be tested without the Amplify client.

import { fetchDatasetQuery, DATASET_CHART_MAX_POINTS } from "./datasetQueryApi";
import type { DatasetQueryParams, DatasetQueryResult } from "./datasetQueryApi";
import { DAY_MS, DEFAULT_RANGE_LOOKBACK_DAYS, pointsFrom, windowQueryParams } from "./seriesSources";
import type { SeriesLine, SeriesRef, SeriesWindow } from "./seriesSources";

const query = (ref: SeriesRef, params: DatasetQueryParams): Promise<Omit<DatasetQueryResult, "datasetId">> => fetchDatasetQuery(ref.id, params);

/**
 * One column over a window. `window` null means the server's default (the last 30 days ending at the newest
 * data); a lookback other than 30 then costs a second call once the newest time is known, which is how a
 * no-base view honours the source's lookback without knowing the data end up front (decision 2).
 */
export async function fetchSeriesWindow(
  ref: SeriesRef,
  window: SeriesWindow | null,
  { lookbackDays = DEFAULT_RANGE_LOOKBACK_DAYS, maxPoints = DATASET_CHART_MAX_POINTS }: { lookbackDays?: number; maxPoints?: number } = {}
): Promise<SeriesLine> {
  const base: DatasetQueryParams = { columns: [ref.column], maxPoints };
  let answer = await query(ref, window ? { ...base, ...windowQueryParams(window) } : base);
  if (!window && lookbackDays !== DEFAULT_RANGE_LOOKBACK_DAYS && answer.range?.end) {
    const end = new Date(answer.range.end).getTime();
    if (Number.isFinite(end)) {
      answer = await query(ref, { ...base, start: new Date(end - lookbackDays * DAY_MS).toISOString(), end: answer.range.end });
    }
  }
  const column = answer.columns.find((c) => c.name.toLowerCase() === ref.column.toLowerCase());
  const points = pointsFrom(answer, column?.name ?? ref.column);
  return {
    ...points,
    unit: column?.unit || undefined,
    label: column?.label || undefined,
    kind: column?.kind ?? null,
    aggregation: answer.aggregation?.applied ? answer.aggregation : null,
  };
}
